// ═══════════════════════════════════════════════════════════════════════════
// Settings
// Extraído VERBATIM de index.html v0.9.16.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { App } from "../App.jsx";
import { FILE_SYSTEM_SUPPORTED } from "../datos/archivo.js";
import { migrateInvoices, migrateProducts } from "../datos/migraciones.js";
import { estadoLlaveFiscal, rotarLlaveFiscal,
         verificarLlaveFiscal } from "../fiscal/timbrado-servidor.js";
import { REMITENTE } from "../pdf/comunes.js";
import { Card, Label, NumInput, SectionTitle } from "./basicos.jsx";
import { getEmpresa, agregarCuenta, quitarCuenta } from "../datos/empresa.js";
import { puedeMembresia, motivoBloqueo } from "../auth/permisos.js";
import { limpiarCache } from "../datos/lectura.js";
import { PanelBitacora } from "./EstadoBitacora.jsx";
import { resumenBitacora } from "../datos/bitacora.js";
import { btnPrimary, btnSecondary, inputStyle } from "./estilos.js";
import { IS_MOBILE } from "./utiles.js";

// ── El cerrojo de la pantalla ──────────────────────────────────────────────
//
// El segundo, a propósito. El primero está en el ruteo (`tabPermitida`), pero
// esta es la pantalla de las llaves de Facturapi, los datos del remitente, el
// folio, el respaldo y la importación: si mañana alguien agrega otro atajo que
// haga `setTab("settings")` sin acordarse del rol, aquí se detiene igual.
//
// Que quede claro lo que esto NO es: no es seguridad. El control real vive en
// la RLS. Esto impide el accidente y la curiosidad, no al que va con intención.
//
// (La llave de Facturapi ya NO está detrás de esta puerta: se timbra desde el
// servidor y la llave vive en el vault. Lo que queda aquí son los datos de la
// vinícola, el folio y el respaldo.)
export function Settings(props) {
  if (!puedeMembresia(props.membresia, "ver_configuracion")) {
    return (
      <div>
        <SectionTitle sub="Esta pantalla no es de tu rol">Configuración</SectionTitle>
        <Card>
          <div style={{ fontSize: 13, color: "#888", lineHeight: 1.8 }}>
            🔒 {motivoBloqueo(props.membresia?.role, "ver_configuracion")}
            <br />
            <span style={{ fontSize: 12, color: "#666" }}>
              Aquí se guardan las llaves fiscales, los datos de la vinícola y los
              respaldos. Si necesitas algo de esto, pídeselo a quien la administra.
            </span>
          </div>
        </Card>
      </div>
    );
  }
  return <Configuracion {...props} />;
}

function Configuracion({ membresia, folio, setFolio, clients, setClients, products, setProducts,
                        invoices, setInvoices }) {
  const [newFolio, setNewFolio] = useState(folio);
  // Datos de la vinícola — lote 2.5. Hoy banco y CLABE viven HARDCODEADOS en
  // la constante REMITENTE; aquí se vuelven editables. Antecesor de `tenant`
  // y `bank_account` del modelo multi-inquilino.
  const [empresa, setEmpresa] = useState(getEmpresa());
  const [ctaEtiqueta, setCtaEtiqueta] = useState("");
  const [ctaBanco, setCtaBanco] = useState("");
  const [ctaClabe, setCtaClabe] = useState("");
  const [ctaError, setCtaError] = useState("");
  // ── La llave fiscal ya no se administra desde aquí ────────────────────────
  //
  // Había dos tarjetas: la llave de Facturapi con su lista de proxies de CORS,
  // y la administración de la organización. Las dos son del tiempo en que se
  // timbraba DESDE EL NAVEGADOR, y eso se acabó: hoy timbra el servidor
  // (Edge Function `timbrar`), la llave vive en el vault de Supabase y nunca
  // baja al dispositivo.
  //
  // El operador lo dijo corto: "se timbra en supabase". Dejar la pantalla era
  // ofrecer una palanca desconectada — y peor, invitaba a pegar la llave live
  // en el navegador otra vez, que es justo la deuda §9 del handover.
  //
  // Lo que queda es SABER, no configurar: si hay llave allá, si es live y si
  // la carta está firmada.
  const [fiscal, setFiscal] = useState(undefined);   // undefined = preguntando
  const [rotando, setRotando] = useState(false);
  // Comprobar la llave sin timbrar. Antes, la unica prueba de que una llave
  // recien puesta servia era un CFDI real con un cliente esperando.
  const [prueba, setPrueba] = useState(null);   // null | "probando" | {ok, ...}
  useEffect(() => {
    const t = membresia?.tenant_id;
    if (!t) { setFiscal(null); return; }
    let vivo = true;
    estadoLlaveFiscal(t).then(e => { if (vivo) setFiscal(e); }).catch(() => vivo && setFiscal(null));
    return () => { vivo = false; };
  }, [membresia?.tenant_id]);

  // ── Vaciar la copia local: rutina, no peligro ─────────────────────────────
  //
  // Esto se llamaba "Zona de Peligro" y pedía teclear BORRAR TODO, la
  // contraseña, y descargaba un JSON prometiendo "para deshacer: Importar ese
  // archivo". Tres cosas mal, dichas por el operador:
  //
  //   · Ya no es peligroso. Cuando existía el auto-push a KV, un clic vaciaba
  //     TODOS los dispositivos. Hoy la verdad está en Postgres y esto solo tira
  //     la copia de aquí: al recargar vuelve entera.
  //   · La promesa era FALSA. No hay ningún botón de Importar en la app, así
  //     que ese archivo no se podía volver a meter por ningún lado.
  //   · Y un JSON descargado no es un mecanismo de vuelta atrás, es un parche.
  //     Si hay que reconstruir algo, se reconstruye desde la base.
  //
  // Lo ÚNICO que se puede perder de verdad es lo que la bitácora no haya subido
  // todavía. Así que se avisa solo cuando eso existe, y se dice cuánto es. Un
  // aviso que sale siempre no lo lee nadie; uno que sale cuando importa, sí.
  const vaciarCopiaLocal = () => {
    const { pendientes, fallidas } = resumenBitacora();
    const sinSubir = pendientes + fallidas;

    if (sinSubir > 0) {
      const seguir = confirm(
        `Hay ${sinSubir} cambio${sinSubir !== 1 ? "s" : ""} que todavía no ` +
        `${sinSubir !== 1 ? "suben" : "sube"} al servidor.\n\n` +
        `Si vacías la copia ahora, eso SÍ se pierde. Todo lo demás vuelve al ` +
        `recargar.\n\n¿Vaciar de todos modos?`);
      if (!seguir) return;
    }

    limpiarCache();
    setClients([]); setProducts([]); setInvoices([]); setFolio(null);
    location.reload();
  };

  return (
    <div>
      <SectionTitle sub="Configuración general del sistema">Configuración</SectionTitle>

      {/* La tarjeta de FUENTE DE DATOS se retiró: ya no hay dos modos.
          Existió para migrar sin riesgo y cumplió. Mantener el interruptor
          después de migrar solo servía para que la misma pantalla contara dos
          verdades distintas según el dispositivo. */}

      {/* ── Timbrado: se informa, no se configura ────────────────────────── */}
      <Card style={{ borderColor: fiscal?.configurada ? "#1a3a1a" : "#1e1e1e" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <div style={{ width: 10, height: 10, borderRadius: "50%",
                        background: fiscal?.configurada ? "#4ade80" : "#555" }} />
          <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, color: "#c9a96e" }}>
            Timbrado CFDI
          </span>
          {fiscal?.configurada && (
            <span style={{ fontSize: 10, letterSpacing: 1, padding: "2px 8px", borderRadius: 4,
                           color: fiscal.es_live ? "#4ade80" : "#facc15",
                           background: "#0d0d0d",
                           border: `1px solid ${fiscal.es_live ? "#1a3a1a" : "#2a2a1a"}` }}>
              {fiscal.es_live ? "TIMBRANDO — CFDI válidos ante el SAT" : "⚠️ PRUEBAS — sin validez fiscal"}
            </span>
          )}
        </div>

        <div style={{ fontSize: 12, color: "#888", lineHeight: 1.8 }}>
          {fiscal === undefined ? "Preguntando al servidor…"
            : fiscal?.configurada ? (
            <>
              🔒 Se timbra <b style={{ color: "#aaa" }}>desde el servidor</b>. La llave vive
              en la bóveda de Supabase y no baja a este dispositivo ni a ningún otro.
              {fiscal.carta_firmada
                ? <> Carta responsiva firmada.</>
                : <> <span style={{ color: "#facc15" }}>Falta la carta responsiva:
                     sin ella no se puede timbrar.</span></>}
            </>
          ) : (
            <>Esta vinícola todavía no tiene llave fiscal en el servidor. Sin ella no se
               puede timbrar.</>
          )}
        </div>

        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap",
                      marginTop: 14, paddingTop: 12, borderTop: "1px solid #1a1a1a" }}>
          {puedeMembresia(membresia, "config_facturapi") ? (
            <>
              <button onClick={() => setRotando(true)}
                style={{ ...btnSecondary, padding: "9px 18px", fontSize: 12 }}>
                Cambiar la llave
              </button>
              {fiscal?.configurada && (
                <button
                  onClick={async () => {
                    setPrueba("probando");
                    try { setPrueba(await verificarLlaveFiscal(membresia.tenant_id)); }
                    catch (e) { setPrueba({ ok: false, detalle: e.message }); }
                  }}
                  disabled={prueba === "probando"}
                  style={{ ...btnSecondary, padding: "9px 18px", fontSize: 12 }}>
                  {prueba === "probando" ? "Probando…" : "Probar la llave"}
                </button>
              )}
              <span style={{ fontSize: 11, color: "#556", flex: 1, minWidth: 200 }}>
                {fiscal?.rotada_en
                  ? <>Última vez: {new Date(fiscal.rotada_en).toLocaleDateString("es-MX")}</>
                  : <>Nunca se ha cambiado desde aquí.</>}
                {" "}Va directo a la bóveda del servidor: no se guarda en este
                dispositivo ni se puede volver a leer desde la app.
              </span>
            </>
          ) : (
            <span style={{ fontSize: 11, color: "#556" }}>
              La llave fiscal la cambia el dueño de la vinícola.
            </span>
          )}
        </div>

        {prueba && prueba !== "probando" && (
          <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 6, fontSize: 12,
                        lineHeight: 1.7,
                        background: prueba.ok ? "#0d1f0d" : "#1f0d0d",
                        border: `1px solid ${prueba.ok ? "#1a3a1a" : "#3a1a1a"}`,
                        color: prueba.ok ? "#a8d060" : "#e8b0b0" }}>
            {prueba.ok ? (
              <>✓ Facturapi reconoce esta llave
                {prueba.organizacion && <> · organización <b>{prueba.organizacion}</b></>}
                {prueba.es_prueba && <> · es de <b>pruebas</b>, no timbra CFDI reales</>}
              </>
            ) : (
              <>⚠️ {prueba.detalle || "Facturapi no reconoce esta llave."}</>
            )}
          </div>
        )}
      </Card>

      {rotando && (
        <RotarLlave tenantId={membresia?.tenant_id} eraLive={fiscal?.es_live}
          onCerrar={() => setRotando(false)}
          onHecho={(e) => { setFiscal(f => ({ ...(f || {}), ...e, configurada: true }));
                            setRotando(false); }} />
      )}

      {/* ── Datos de la vinícola ─────────────────────────────────────────── */}
      <Card>
        <div id="datos-vinicola" style={{ fontSize: 11, color: "#70c9c9", letterSpacing: 2, marginBottom: 12 }}>
          🏛 DATOS DE LA VINÍCOLA
        </div>
        <div style={{ fontSize: 12, color: "#888", marginBottom: 14 }}>
          {empresa.nombre}{empresa.rfc ? ` · ${empresa.rfc}` : ""}
        </div>

        <div style={{ fontSize: 10, color: "#666", letterSpacing: 1, marginBottom: 8 }}>CUENTAS BANCARIAS</div>
        {empresa.cuentas.filter(c => c.activa !== false).length === 0 ? (
          <div style={{ padding: "8px 12px", background: "#1f1a0d", borderRadius: 6, fontSize: 12, color: "#c9a96e", marginBottom: 12 }}>
            No hay cuentas capturadas. Sin ellas no se puede registrar a qué cuenta entró un pago.
          </div>
        ) : (
          <div style={{ marginBottom: 12 }}>
            {empresa.cuentas.filter(c => c.activa !== false).map(c => (
              <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0",
                borderBottom: "1px solid #161616", fontSize: 12 }}>
                <span style={{ color: "#e8e0d4", minWidth: 160 }}>{c.etiqueta}</span>
                <span style={{ color: "#666", flex: 1 }}>
                  {c.banco}{c.clabe ? ` · CLABE ${c.clabe}` : ""}
                </span>
                <button onClick={() => { if (confirm(`¿Dar de baja "${c.etiqueta}"?\n\nLos pagos ya registrados la conservan.`)) setEmpresa(quitarCuenta(c.id)); }}
                  style={{ background: "none", border: "1px solid #3a1a1a", borderRadius: 4, color: "#c97070",
                    cursor: "pointer", padding: "2px 8px", fontSize: 10 }}>Dar de baja</button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1.2fr auto", gap: 8, alignItems: "end" }}>
          <div>
            <Label>Nombre corto</Label>
            <input value={ctaEtiqueta} onChange={e => setCtaEtiqueta(e.target.value)}
              placeholder="Banbajío ···2386" style={{ ...inputStyle, width: "100%" }} />
          </div>
          <div>
            <Label>Banco</Label>
            <input value={ctaBanco} onChange={e => setCtaBanco(e.target.value)}
              placeholder="Banco del Bajío" style={{ ...inputStyle, width: "100%" }} />
          </div>
          <div>
            <Label>CLABE</Label>
            <input value={ctaClabe} onChange={e => setCtaClabe(e.target.value)} inputMode="numeric"
              placeholder="18 dígitos" style={{ ...inputStyle, width: "100%" }} />
          </div>
          <button onClick={() => {
            try {
              setEmpresa(agregarCuenta({ etiqueta: ctaEtiqueta, banco: ctaBanco, clabe: ctaClabe }));
              setCtaEtiqueta(""); setCtaBanco(""); setCtaClabe(""); setCtaError("");
            } catch (e) { setCtaError(e.message); }
          }} style={{ ...btnSecondary, padding: "8px 14px", fontSize: 12 }}>+ Agregar</button>
        </div>
        {ctaError && <div style={{ color: "#c97070", fontSize: 12, marginTop: 8 }}>⚠️ {ctaError}</div>}

        <div style={{ marginTop: 12, fontSize: 10, color: "#555" }}>
          Estas cuentas se ofrecen al registrar pagos en Cuentas. El domicilio fiscal, la CSF de la
          vinícola y los domicilios de bodega viven aquí también cuando llegue el modelo multi‑inquilino.
        </div>
      </Card>


      {/* La sección de sincronización se retiró junto con KV.
          Lo que sube ahora lo lleva la bitácora: sube sola, sabe qué cambió y
          se ve en el indicador de la barra superior. Un token que configurar y
          un botón de sincronizar eran justo lo que sobraba de la etapa en que
          el dispositivo era la fuente. */}
      <Card><PanelBitacora /></Card>


      <Card style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, color: "#c9a96e", marginBottom: 16 }}>Número de Folio</div>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
          <div style={{ flex: 1, maxWidth: 200 }}>
            <Label>Próximo folio</Label>
            <NumInput value={newFolio} onChange={v => setNewFolio(v)} style={inputStyle} integer />
          </div>
          <button onClick={() => { setFolio(newFolio); alert("Folio actualizado"); }} style={btnPrimary}>Actualizar</button>
        </div>
      </Card>

      <Card style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, color: "#c9a96e", marginBottom: 16 }}>Datos del Remitente</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px", fontSize: 13 }}>
          <div><span style={{ color: "#666" }}>Nombre:</span> {REMITENTE.name}</div>
          <div><span style={{ color: "#666" }}>RFC:</span> {REMITENTE.rfc}</div>
          <div><span style={{ color: "#666" }}>Dirección:</span> {REMITENTE.street}</div>
          <div><span style={{ color: "#666" }}>Colonia:</span> {REMITENTE.colonia}, CP {REMITENTE.cp}</div>
          <div><span style={{ color: "#666" }}>Ciudad:</span> {REMITENTE.city}, {REMITENTE.state}</div>
          <div><span style={{ color: "#666" }}>Banco:</span> {REMITENTE.bank}</div>
          <div><span style={{ color: "#666" }}>CLABE:</span> {REMITENTE.clabe}</div>
          <div><span style={{ color: "#666" }}>WhatsApp:</span> {REMITENTE.whatsapp}</div>
        </div>
        <p style={{ fontSize: 11, color: "#555", marginTop: 12 }}>Para modificar los datos del remitente, contacta al desarrollador.</p>
      </Card>

      <Card style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, color: "#c9a96e", marginBottom: 16 }}>Estadísticas</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, textAlign: "center" }}>
          <div style={{ padding: 16, background: "#0d0d0d", borderRadius: 8 }}>
            <div style={{ fontSize: 28, fontWeight: 600, color: "#c9a96e" }}>{clients.length}</div>
            <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>Clientes</div>
          </div>
          <div style={{ padding: 16, background: "#0d0d0d", borderRadius: 8 }}>
            <div style={{ fontSize: 28, fontWeight: 600, color: "#c9a96e" }}>{products.length}</div>
            <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>Productos</div>
          </div>
          <div style={{ padding: 16, background: "#0d0d0d", borderRadius: 8 }}>
            <div style={{ fontSize: 28, fontWeight: 600, color: "#c9a96e" }}>{invoices.length}</div>
            <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>Remisiones</div>
          </div>
        </div>
      </Card>


      {/* ── PWA INSTALL ── */}
      <Card style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, color: "#c9a96e" }}>📱 Instalar como App</span>
        </div>
        <div style={{ fontSize: 13, color: "#888", marginBottom: 16 }}>
          Vinícola Total es una Progressive Web App (PWA). Puedes instalarla como aplicación nativa en tu teléfono, tablet o computadora.
        </div>
        <button id="pwa-install-btn" onClick={() => window.triggerPWAInstall && window.triggerPWAInstall()}
          style={{ ...btnPrimary, display: "none" }}>
          📲 Instalar Vinícola Total
        </button>
        <div style={{ padding: 12, background: "#111", borderRadius: 6, fontSize: 11, color: "#555" }}>
          <strong style={{ color: "#888" }}>¿Cómo instalar?</strong><br />
          <strong style={{ color: "#c9a96e" }}>Android (Chrome):</strong> Menú ⋮ → "Añadir a pantalla de inicio" o "Instalar app"<br />
          <strong style={{ color: "#c9a96e" }}>iPhone (Safari):</strong> Botón compartir ↗ → "Añadir a pantalla de inicio"<br />
          <strong style={{ color: "#c9a96e" }}>PC (Chrome/Edge):</strong> Click el ícono 📲 en la barra de dirección, o usa el botón de arriba si aparece<br />
          <span style={{ color: "#70c9c9", marginTop: 4, display: "block" }}>La app funciona sin internet una vez instalada.</span>
        </div>
      </Card>

      {/* La zona de peligro entera es del dueño. Un gerente veía el botón
          apagado con un candado: un ofrecimiento que solo sabe decir que no.
          Misma regla que en las acciones de una remisión — lo que el rol no
          puede hacer, no se dibuja. */}
      {puedeMembresia(membresia, "borrar_todo") && (
      <Card>
        {/* Rutina, no peligro: la verdad está en Postgres. */}
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, color: "#c9a96e", marginBottom: 12 }}>
          Vaciar la copia de este dispositivo
        </div>
        <p style={{ fontSize: 13, color: "#888", marginBottom: 8, lineHeight: 1.7 }}>
          Los datos viven en el servidor. Esto tira la copia que este dispositivo
          guarda para trabajar sin red, y al recargar se vuelve a bajar entera.
          <b style={{ color: "#aaa" }}> No borra nada del servidor</b>, ni afecta a
          nadie más.
        </p>
        <p style={{ fontSize: 12, color: "#888", marginBottom: 16, lineHeight: 1.7 }}>
          Sirve cuando este dispositivo enseña algo raro o quedó con datos viejos.
          Lo único que se pierde es lo que la bitácora no haya subido todavía — si
          hay algo pendiente, se avisa antes.
        </p>
        <button onClick={vaciarCopiaLocal}
          style={{ ...btnSecondary, padding: "9px 18px", fontSize: 12 }}>
          Vaciar y recargar
        </button>
      </Card>
      )}
    </div>
  );
}

// ── Cambiar la llave fiscal ────────────────────────────────────────────────
//
// Existe porque al retirar la vieja pantalla de Facturapi dejé al operador sin
// ninguna forma de rotar la llave — y la llave live se había filtrado. Su
// alternativa era pasármela por el chat, o sea filtrarla otra vez.
//
// LO QUE ESTA PANTALLA GARANTIZA, y por eso se dice aquí y en la pantalla:
//
//   · la llave viaja del teclado al vault del servidor y no para en medio.
//     No toca `localStorage`, ni el respaldo, ni la bitácora, ni la respuesta.
//   · no hay forma de volver a leerla desde la app: `leer_llave_fiscal` está
//     revocada para el navegador y solo la usa el servicio de timbrado.
//   · si te equivocas, se vuelve a pegar. Rotar no destruye nada: la anterior
//     deja de servir en Facturapi cuando TÚ la revocas allá, no aquí.
//
// El aviso de LIVE es deliberadamente incómodo. Poner una llave live es
// habilitar comprobantes reales ante el SAT, y confundirla con la de pruebas
// se descubre cuando ya se timbró.
function RotarLlave({ tenantId, eraLive, onCerrar, onHecho }) {
  const [llave, setLlave] = useState("");
  const [esLive, setEsLive] = useState(!!eraLive);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState("");

  // Facturapi distingue sus llaves por prefijo. Si el prefijo y el interruptor
  // no coinciden, casi siempre es que se pegó la que no era.
  const pareceLive = /^sk_live/i.test(llave.trim());
  const pareceTest = /^sk_test/i.test(llave.trim());
  const discrepa = (pareceLive && !esLive) || (pareceTest && esLive);

  const guardar = async () => {
    if (esLive && !confirm(
      "La llave que vas a guardar es de PRODUCCIÓN.\n\n" +
      "A partir de ahora, cada timbrado genera un CFDI real ante el SAT.\n\n" +
      "¿Continuar?")) return;
    setOcupado(true); setError("");
    try {
      const r = await rotarLlaveFiscal(tenantId, llave.trim(), esLive);
      setLlave("");                       // fuera de memoria en cuanto se va
      onHecho?.({ es_live: esLive, rotada_en: r?.rotada_en || new Date().toISOString() });
    } catch (e) { setError(e.message); setOcupado(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.88)", zIndex: 1000,
                  display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={onCerrar}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "#111", maxWidth: 500, width: "100%", borderRadius: 12,
                 padding: 28, border: `1px solid ${esLive ? "#3a1a1a" : "#222"}` }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22,
                      color: "#c9a96e", marginBottom: 6 }}>Cambiar la llave fiscal</div>
        <div style={{ fontSize: 12, color: "#888", lineHeight: 1.8, marginBottom: 18 }}>
          Pégala tal como la copiaste de Facturapi. Va directo a la bóveda del
          servidor: <b style={{ color: "#aaa" }}>no se guarda en este dispositivo</b> y
          no se puede volver a leer desde la app.
        </div>

        <Label>Llave nueva</Label>
        <input type="password" value={llave} autoComplete="off" autoFocus
          onChange={e => { setLlave(e.target.value); setError(""); }}
          placeholder="sk_live_… o sk_test_…"
          style={{ ...inputStyle, fontFamily: "monospace" }} />

        <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
          {[[false, "Pruebas", "Los comprobantes NO tienen validez fiscal."],
            [true,  "Producción", "Cada timbrado es un CFDI real ante el SAT."]].map(([v, t, ayuda]) => (
            <button key={String(v)} onClick={() => setEsLive(v)} title={ayuda}
              style={{ flex: 1, minWidth: 140, padding: "10px 12px", borderRadius: 8,
                       cursor: "pointer", fontFamily: "inherit", fontSize: 12,
                       background: esLive === v ? (v ? "#1f0d0d" : "#1a1810") : "transparent",
                       border: `1px solid ${esLive === v ? (v ? "#5a2a2a" : "#3a3222") : "#222"}`,
                       color: esLive === v ? (v ? "#f87171" : "#c9a96e") : "#666" }}>
              {t}
            </button>
          ))}
        </div>

        {discrepa && (
          <div style={{ fontSize: 12, color: "#facc15", marginTop: 12, lineHeight: 1.7 }}>
            ⚠️ La llave empieza con <code>{pareceLive ? "sk_live" : "sk_test"}</code> pero
            elegiste <b>{esLive ? "Producción" : "Pruebas"}</b>. Revisa cuál pegaste.
          </div>
        )}

        {error && (
          <div style={{ background: "#1f0d0d", border: "1px solid #3a1a1a", borderRadius: 6,
                        padding: "10px 12px", marginTop: 14, fontSize: 12, color: "#e8b0b0" }}>
            ⚠️ {error}
          </div>
        )}

        <div style={{ fontSize: 11, color: "#556", lineHeight: 1.7, marginTop: 16 }}>
          Cambiar la llave aquí <b>no revoca la anterior</b>. Para que la vieja deje de
          servir hay que revocarla en Facturapi — que es justo lo que hay que hacer
          cuando una se filtró.
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "flex-end", marginTop: 20 }}>
          <button onClick={onCerrar} disabled={ocupado} style={btnSecondary}>Cancelar</button>
          <button onClick={guardar} disabled={ocupado || llave.trim().length < 20}
            style={{ ...btnPrimary, opacity: (ocupado || llave.trim().length < 20) ? 0.4 : 1 }}>
            {ocupado ? "Guardando…" : "Guardar la llave"}
          </button>
        </div>
      </div>
    </div>
  );
}
